import { useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    IconButton,
    LinearProgress,
    Alert,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import { addDocumentsToMastersheet, uploadRequestedDocuments } from '../api';

const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `.swal2-container {z-index: 3000 !important;}`;
document.head.appendChild(styleSheet);

const UploadDocument = ({
    isOpen,
    setOpen,
    onClose,
    onDocumentUploaded,
    propertyId,
    buildingName,
    unitNo,
    documentType,
    documents = [],
    isNADisabled
}) => {
    const [file, setFile] = useState(null);
    const [s3Data, setS3Data] = useState({ url: null, key: null });
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    // Filter documents to show only those with alwaysVisible === true
    const availableDocuments = documents.filter(doc => doc.alwaysVisible === false);
    const documentTypes = availableDocuments.map(doc => doc.document_type);

    // Determine if we need to show document type selection
    const showDocumentTypeSelection = !documentType && documentTypes.length > 0;

    const validationSchema = Yup.object({
        document_type: showDocumentTypeSelection
            ? Yup.string()
                .required('Document type is required')
                .oneOf(documentTypes, 'Please select a valid document type')
            : Yup.string(),
        comments: Yup.string().max(500, 'Comments cannot exceed 500 characters'),
        propertyId: Yup.string().test(
            'is-valid-objectid',
            'Invalid property ID',
            (value) => !value || /^[0-9a-fA-F]{24}$/.test(value)
        ),
    });

    const formik = useFormik({
        initialValues: {
            document_type: documentType || '',
            comments: '',
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            if (!file || !s3Data.url || !s3Data.key) {
                setError('File not uploaded to S3');
                return;
            }

            try {
                setUploading(true);

                const documentData = {
                    document_type: documentType || values.document_type,
                    url: s3Data.url,
                    key: s3Data.key,
                    comments: values.comments || undefined,
                    size: file.size,
                    previous_versions: [],
                };

                const response = await addDocumentsToMastersheet(
                    propertyId || undefined,
                    buildingName || undefined,
                    unitNo || undefined,
                    documentData
                );

                setUploading(false);
                onClose();
                onDocumentUploaded(response.data);

                Swal.fire({
                    title: 'Document Added',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    customClass: {
                        container: 'swal2-container',
                    },
                });
            } catch (err) {
                console.error('Error adding document:', err);
                setError(err.response?.data?.message || 'Failed to add document');
                setUploading(false);
            }
        }
    });

    const uploadToS3 = async (selectedFile) => {
        try {
            setUploading(true);
            setUploadProgress(0);

            const formData = new FormData();
            formData.append('documents', selectedFile);

            const s3Response = await uploadRequestedDocuments(formData, (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(progress);
                }
            });

            const { url, key } = s3Response.data[0];
            setS3Data({ url, key });
            setUploading(false);
            setError(null);
        } catch (err) {
            console.error('S3 upload error:', err);
            setError('Failed to upload file to S3');
            setUploading(false);
            setS3Data({ url: null, key: null });
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setError(null);
            uploadToS3(selectedFile);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const droppedFile = event.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            setError(null);
            uploadToS3(droppedFile);
        }
    };

    const resetUploader = () => {
        setFile(null);
        setS3Data({ url: null, key: null });
        setUploadProgress(0);
        setError(null);
        formik.resetForm();
        setOpen(true);
    };

    const handleCancel = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Are you sure you want to stop uploading this document?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, stop upload',
            cancelButtonText: 'No, continue',
            customClass: {
                container: 'swal2-container',
            },
        }).then((result) => {
            if (result.isConfirmed) {
                setUploading(false);
                onClose();
            }
        });
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, p: 1 },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Add Document</Typography>
                <IconButton onClick={handleCancel} disabled={uploading}>
                    <CancelIcon fontSize="medium" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={formik.handleSubmit} disabled={isNADisabled}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        {/* Show document type selection only if documentType is not provided */}
                        {showDocumentTypeSelection && (
                            <FormControl fullWidth>
                                <InputLabel id="document-type-label">Document Type</InputLabel>
                                <Select
                                    labelId="document-type-label"
                                    id="document_type"
                                    name="document_type"
                                    value={formik.values.document_type}
                                    label="Document Type"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.document_type && Boolean(formik.errors.document_type)}
                                    disabled={uploading}
                                >
                                    {documentTypes.map((docType) => (
                                        <MenuItem key={docType} value={docType}>
                                            {docType}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formik.touched.document_type && formik.errors.document_type && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                                        {formik.errors.document_type}
                                    </Typography>
                                )}
                            </FormControl>
                        )}

                        {/* Show document type as read-only if provided */}
                        {documentType && (
                            <TextField
                                fullWidth
                                label="Document Type"
                                value={documentType}
                                disabled
                                variant="outlined"
                            />
                        )}

                        <TextField
                            fullWidth
                            id="comments"
                            name="comments"
                            label="Comments (Optional)"
                            multiline
                            rows={3}
                            value={formik.values.comments}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.comments && Boolean(formik.errors.comments)}
                            helperText={formik.touched.comments && formik.errors.comments}
                            disabled={uploading}
                        />

                        <FormControl>
                            <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1, ml: -1 }}>
                                File Upload
                            </InputLabel>
                            <Box
                                sx={{
                                    border: '2px dashed',
                                    borderColor: 'grey.400',
                                    borderRadius: 2,
                                    p: 3,
                                    textAlign: 'center',
                                    bgcolor: 'grey.50',
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: 'grey.600' },
                                }}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    disabled={uploading}
                                />
                                <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500', mb: 2 }} />
                                <Typography variant="body1" sx={{ mt: 2 }}>
                                    {file ? file.name : 'Drag & drop file here or click to browse'}
                                </Typography>
                                {file && (
                                    <Typography variant="body2" color="text.secondary">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </Typography>
                                )}
                            </Box>
                        </FormControl>
                    </Box>

                    {uploading && (
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Uploading...</Typography>
                                <Typography variant="body2">{uploadProgress}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                    )}
                </form>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button onClick={handleCancel} disabled={uploading}>
                    Cancel
                </Button>
                <Button
                    onClick={formik.handleSubmit}
                    variant="contained"
                    disabled={!file || !s3Data.url || uploading || (showDocumentTypeSelection && !formik.values.document_type)}
                    startIcon={<CloudUploadIcon />}
                >
                    Add Document
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { UploadDocument };